import { useCallback, useEffect, useRef, useState } from "react";

const IMPRESSIONS_KEY = "jvvnl_search_discovery_impressions";
const COMPLETED_KEY = "jvvnl_search_discovery_completed";
const MAX_IMPRESSIONS = 3;

/** Hover dwell before the tooltip arms (short accidental hovers ignored). */
export const HOVER_DELAY_MS = 700;
/** Safety-net cleanup: hide after this long with no interaction at all. */
const AUTO_HIDE_MS = 8000;
/** Loss-of-interest: hide this long after the pointer leaves the search region. */
const POINTER_LEAVE_HIDE_MS = 2000;

export type DiscoveryVariant = "full" | "compact";

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — degrade silently */
  }
}

export interface SearchDiscovery {
  /** Tooltip should render now. */
  visible: boolean;
  /** Which layout to show — full (impressions 0-1) or compact one-liner (3rd show). */
  variant: DiscoveryVariant;
  /** Whether onboarding can still be shown (not completed, <3 impressions, not suppressed). */
  eligible: boolean;
  /** Arm discovery after a qualifying intent (hover dwell / keyboard focus). */
  arm: () => void;
  /** Cancel a pending hover-arm (pointer left before the dwell completed). */
  cancelArm: () => void;
  /** Pointer entered the search region (trigger or tooltip) — cancel the leave-hide. */
  pointerEnter: () => void;
  /** Pointer left the search region — hide after a short grace (loss of interest). */
  pointerLeave: () => void;
  /** Soft hide — re-armable this session (2s-leave / 8s timeout / scroll). */
  hideSoft: () => void;
  /** Hard hide — suppress for the rest of the page session (ESC / click-outside). */
  suppressForSession: () => void;
  /** Persistent completion — never show again on any future visit. */
  complete: () => void;
}

/**
 * Owns the first-time search-discovery state.
 *
 * Persistent (localStorage): `completed` + `impressions` (count of times the
 * tooltip has ACTUALLY been shown — incremented on show, never on page load,
 * so a user who never hovers keeps all exposures).
 *
 * Session-only (in-memory): armed/visible flags + timers, and a hard
 * `suppressedForSession` flag.
 *
 * Discovery is NOT shown on page load — it is armed by user intent (a 700ms
 * hover dwell or keyboard focus on the search trigger). Once shown, two timers
 * can hide it: a 2s pointer-leave (loss of interest) and an 8s safety net. Both
 * are SOFT hides — a later deliberate re-hover re-arms it (until 3 impressions
 * are used up or onboarding completes). Only ESC / click-outside suppress the
 * rest of the session.
 *
 * Exposure schedule: impressions 1-2 → full tooltip, impression 3 → compact
 * one-liner, then never.
 */
export function useSearchDiscovery(): SearchDiscovery {
  const [impressions, setImpressions] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [armed, setArmed] = useState(false);
  const [suppressedForSession, setSuppressedForSession] = useState(false);

  const didInit = useRef(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Guard against React StrictMode double-invoke. NOTE: unlike the old model
    // this does NOT increment anything on mount — impressions advance on show.
    if (didInit.current) return;
    didInit.current = true;

    if (safeGet(COMPLETED_KEY) === "true") {
      setCompleted(true);
      return;
    }
    const stored = parseInt(safeGet(IMPRESSIONS_KEY) || "0", 10) || 0;
    setImpressions(stored);
  }, []);

  const clearHover = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);

  const clearAutoHide = useCallback(() => {
    if (autoHideTimer.current) {
      clearTimeout(autoHideTimer.current);
      autoHideTimer.current = null;
    }
  }, []);

  const clearPointerLeave = useCallback(() => {
    if (pointerLeaveTimer.current) {
      clearTimeout(pointerLeaveTimer.current);
      pointerLeaveTimer.current = null;
    }
  }, []);

  const clearTimers = useCallback(() => {
    clearHover();
    clearAutoHide();
    clearPointerLeave();
  }, [clearHover, clearAutoHide, clearPointerLeave]);

  // Soft hide: re-armable this session (timers / scroll). Does not suppress.
  const hideSoft = useCallback(() => {
    clearTimers();
    setArmed(false);
  }, [clearTimers]);

  // Hard hide: suppress the rest of the page session (ESC / click-outside).
  const suppressForSession = useCallback(() => {
    clearTimers();
    setArmed(false);
    setSuppressedForSession(true);
  }, [clearTimers]);

  const arm = useCallback(() => {
    if (hoverTimer.current) return; // dwell already pending
    if (armed) return; // already shown — don't double-count or re-time
    // Re-check eligibility at fire time (count may have hit MAX on a prior show).
    if (completed || suppressedForSession) return;
    if ((parseInt(safeGet(IMPRESSIONS_KEY) || "0", 10) || 0) >= MAX_IMPRESSIONS)
      return;
    hoverTimer.current = setTimeout(() => {
      hoverTimer.current = null;
      // Count this as an impression (on show — never on page load) and persist.
      setImpressions((prev) => {
        const next = prev + 1;
        safeSet(IMPRESSIONS_KEY, String(next));
        return next;
      });
      setArmed(true);
      // 8s safety net starts once on show; not reset by interaction.
      clearAutoHide();
      autoHideTimer.current = setTimeout(hideSoft, AUTO_HIDE_MS);
    }, HOVER_DELAY_MS);
  }, [armed, completed, suppressedForSession, clearAutoHide, hideSoft]);

  const cancelArm = useCallback(() => {
    // Only cancels a not-yet-fired hover dwell. A shown tooltip is governed by
    // the pointer-leave / auto-hide timers, not by this.
    clearHover();
  }, [clearHover]);

  // Pointer entered the search region (trigger or tooltip): cancel the leave-hide.
  const pointerEnter = useCallback(() => {
    clearPointerLeave();
  }, [clearPointerLeave]);

  // Pointer left the region: soft-hide after a short grace (responsive cleanup).
  const pointerLeave = useCallback(() => {
    clearPointerLeave();
    pointerLeaveTimer.current = setTimeout(hideSoft, POINTER_LEAVE_HIDE_MS);
  }, [clearPointerLeave, hideSoft]);

  const complete = useCallback(() => {
    clearTimers();
    safeSet(COMPLETED_KEY, "true");
    setCompleted(true);
    setArmed(false);
  }, [clearTimers]);

  // Clean up timers on unmount.
  useEffect(() => clearTimers, [clearTimers]);

  // `eligible` gates ARMING: onboarding may still be shown — not completed,
  // fewer than 3 impressions used so far, and not hard-suppressed this session.
  const eligible =
    !completed && impressions < MAX_IMPRESSIONS && !suppressedForSession;
  // `visible` is keyed on `armed` alone, NOT on `eligible`: arming already
  // incremented `impressions` (which can push it to MAX and flip `eligible`
  // false), and the just-armed tooltip must stay rendered for this show.
  // `armed` is only ever set true while arming was permitted, so suppression /
  // completion (which also clear `armed`) still hide it.
  const visible = armed && !completed && !suppressedForSession;
  // The 3rd impression (count === MAX) shows the compact one-liner; 1st/2nd full.
  const variant: DiscoveryVariant =
    impressions >= MAX_IMPRESSIONS ? "compact" : "full";

  return {
    visible,
    variant,
    eligible,
    arm,
    cancelArm,
    pointerEnter,
    pointerLeave,
    hideSoft,
    suppressForSession,
    complete,
  };
}
